import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CartItem } from "../catalog/types";
import { useCatalog } from "../catalog/CatalogContext";
import { useCurrency } from "../currency/CurrencyContext";

interface CartContextValue {
  items: CartItem[];
  count: number;
  subtotal: number;
  add: (productId: string, qty?: number) => void;
  remove: (productId: string) => void;
  setQuantity: (productId: string, qty: number) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);
// Nueva clave para no restaurar IDs pertenecientes a los productos de muestra eliminados.
const STORAGE_KEY = "socrattica.cart.v2";

export function CartProvider({ children }: { children: ReactNode }) {
  const { getProduct } = useCatalog();
  const { toCop } = useCurrency();
  const [items, setItems] = useState<CartItem[]>([]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as CartItem[];
        if (Array.isArray(parsed)) {
          setItems(
            parsed
              .filter((item) => typeof item?.productId === "string")
              .map((item) => ({ productId: item.productId, quantity: 1 })),
          );
        }
      }
    } catch {
      // Un carrito local corrupto se ignora y comienza vacío.
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Algunos navegadores bloquean localStorage; el carrito sigue funcionando en memoria.
    }
  }, [items, hydrated]);

  const value = useMemo<CartContextValue>(() => {
    const subtotal = items.reduce((acc, it) => {
      const p = getProduct(it.productId);
      return acc + (p ? toCop(p.price * it.quantity, p.currency) : 0);
    }, 0);
    return {
      items,
      count: items.reduce((a, i) => a + i.quantity, 0),
      subtotal,
      add: (productId) =>
        setItems((prev) => {
          const existing = prev.find((i) => i.productId === productId);
          if (existing) return prev;
          return [...prev, { productId, quantity: 1 }];
        }),
      remove: (productId) => setItems((prev) => prev.filter((i) => i.productId !== productId)),
      setQuantity: (productId, qty) =>
        setItems((prev) =>
          qty <= 0
            ? prev.filter((i) => i.productId !== productId)
            : prev.map((i) => (i.productId === productId ? { ...i, quantity: 1 } : i)),
        ),
      clear: () => setItems([]),
    };
  }, [getProduct, items, toCop]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
